/* ============================================================
   Transforming Industries — horizontal accordion behaviour
   ------------------------------------------------------------
   Markup is static in index.html (edit the copy/images there).
   This only toggles .is-active: clicking a strip expands it and
   collapses the rest. Click only — hover must not expand.
   Keyboard: focus a strip and press Enter/Space. The in-panel
   prev/next buttons step to the neighbouring industry.

   On mobile (≤991px, vertical accordion) two extras kick in:
   - the panel is slid open/closed via an explicit height transition
     (CSS alone can't animate to/from "auto").
   - the clicked strip is scrolled to sit just under the fixed header,
     so expanding/collapsing panels never push it out of view.
   ============================================================ */
export function initIndustriesAccordion() {
  var row = document.querySelector("[data-industries]");
  if (!row) return;

  var items = Array.prototype.slice.call(row.querySelectorAll(".ind-item"));
  if (!items.length) return;

  var mql = window.matchMedia("(max-width: 991px)");
  function isMobile() {
    return mql.matches;
  }

  function panelOf(item) {
    return item.querySelector(".ind-panel");
  }

  /* Freeze the panel's current rendered height as a px value, then drop
     it to 0 so the height change (not "auto") is what actually animates. */
  function collapse(item) {
    var panel = panelOf(item);
    if (!panel || !isMobile()) return;
    panel.style.height = panel.scrollHeight + "px";
    void panel.offsetHeight;
    panel.style.height = "0px";
  }

  /* Same trick in reverse: start pinned at 0, then animate to the panel's
     natural content height, and hand back to CSS ("auto") once settled
     so late-loading images/content aren't clipped. */
  function expand(item) {
    var panel = panelOf(item);
    if (!panel || !isMobile()) return;
    panel.style.height = "0px";
    void panel.offsetHeight;
    panel.style.height = panel.scrollHeight + "px";
    panel.addEventListener("transitionend", function onEnd(e) {
      if (e.target !== panel || e.propertyName !== "height") return;
      panel.style.height = "";
      panel.removeEventListener("transitionend", onEnd);
    });
  }

  /* Keep the clicked strip in view: scroll it to sit just below the
     fixed header instead of letting the expand/collapse reflow carry
     it out of the viewport. */
  function scrollIntoPlace(item) {
    if (!isMobile()) return;
    var header = document.querySelector(".site-header");
    var offset = (header ? header.getBoundingClientRect().bottom : 0) + 16;
    var top = item.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top: top, behavior: "smooth" });
  }

  /* Crossing the mobile breakpoint mid-session (resize/rotate) can leave
     a stale inline px height behind; drop it so CSS retakes control. */
  mql.addEventListener("change", function () {
    items.forEach(function (i) {
      var panel = panelOf(i);
      if (panel) panel.style.height = "";
    });
  });

  function activate(item) {
    if (item.classList.contains("is-active")) {
      scrollIntoPlace(item);
      return;
    }

    var previouslyActive = items.filter(function (i) {
      return i.classList.contains("is-active");
    });

    items.forEach(function (i) {
      i.classList.remove("is-active");
      i.setAttribute("aria-expanded", "false");
    });
    item.classList.add("is-active");
    item.setAttribute("aria-expanded", "true");

    previouslyActive.forEach(collapse);
    expand(item);
    scrollIntoPlace(item);
  }

  function activateByOffset(current, delta) {
    var idx = items.indexOf(current);
    activate(items[(idx + delta + items.length) % items.length]);
  }

  items.forEach(function (item) {
    item.addEventListener("click", function (e) {
      /* let the nav buttons and the case-study link do their own thing */
      if (e.target.closest(".slider-btn") || e.target.closest(".ind-case")) return;
      activate(item);
    });

    item.addEventListener("keydown", function (e) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        activate(item);
      }
    });

    var prev = item.querySelector("[data-ind-prev]");
    var next = item.querySelector("[data-ind-next]");
    if (prev) {
      prev.addEventListener("click", function () {
        activateByOffset(item, -1);
      });
    }
    if (next) {
      next.addEventListener("click", function () {
        activateByOffset(item, 1);
      });
    }
  });
}
